import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md font-label transition-colors duration-fast ease-standard active:scale-press focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active focus-visible:shadow-focus",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-border active:bg-border-strong focus-visible:shadow-focus",
        outline:
          "border border-input bg-background text-foreground hover:bg-surface-subtle hover:border-border-strong active:bg-surface-sunken focus-visible:shadow-focus",
        ghost:
          "text-muted-foreground hover:bg-surface-sunken hover:text-foreground active:bg-border focus-visible:shadow-focus",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive-hover active:bg-destructive-text focus-visible:shadow-focus-danger",
        // Deliberate deviation from the reference: destructive-soft keeps the danger ring, not a normal one.
        "destructive-soft":
          "border border-border text-muted-foreground hover:bg-destructive-subtle hover:text-destructive-text hover:border-destructive-border active:bg-destructive-subtle-strong focus-visible:shadow-focus-danger",
        link: "text-primary underline-offset-4 hover:text-primary-hover hover:underline active:text-primary-active focus-visible:shadow-focus",
      },
      size: {
        sm: "h-9 gap-1.5 px-3 text-sm [&_svg]:size-4",
        md: "h-10 gap-2 px-4 text-sm [&_svg]:size-4",
        lg: "h-11 gap-2 px-6 text-base [&_svg]:size-[18px]",
      },
      fullWidth: {
        true: "w-full",
      },
    },
    compoundVariants: [{ variant: "link", class: "h-auto p-0" }],
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

type ButtonBaseProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

/**
 * Props for the default `<button>` rendering.
 * - `loading` replaces the label with a spinner and disables the control.
 */
type ButtonOwnProps = ButtonBaseProps & {
  asChild?: false;
  loading?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
};

/**
 * Props when rendering the child element instead of a `<button>` (Radix triggers,
 * router links). A documented extension to the design-system contract.
 * - `loading`/`leadingIcon`/`trailingIcon` are `never` here rather than merely
 *   undocumented: Slot takes exactly one child, which the caller owns, so the
 *   component cannot inject a spinner or icons. Allowing them compiled to an
 *   enabled, un-spinnered control during an in-flight mutation.
 * - `disabled` is forwarded, and also mirrored to `aria-disabled` because the
 *   child is usually an `<a>`, where `disabled` is inert and `:disabled` never
 *   matches — without it a "disabled" link stays clickable.
 * - `aria-disabled` is `never` for the same reason `loading` is: a caller-supplied
 *   value would desync it from `disabled`, producing a clickable "disabled" link.
 */
type ButtonAsChildProps = Omit<ButtonBaseProps, "aria-disabled"> & {
  asChild: true;
  "aria-disabled"?: never;
  loading?: never;
  leadingIcon?: never;
  trailingIcon?: never;
};

export type ButtonProps = ButtonOwnProps | ButtonAsChildProps;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      asChild = false,
      loading = false,
      leadingIcon,
      trailingIcon,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    if (asChild) {
      // Slot's own prop type has no `disabled`; it forwards unknown props straight to
      // the child, which is what a disabled trigger or router link needs. Spread last
      // so the aria mirror cannot be overridden by a caller's props.
      const disabledProps = { disabled, "aria-disabled": disabled || undefined };
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, fullWidth, className }))}
          ref={ref}
          {...props}
          {...disabledProps}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" aria-hidden="true" />
            Processing…
          </>
        ) : (
          <>
            {leadingIcon}
            {children}
            {trailingIcon}
          </>
        )}
      </button>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
